import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { CloudUpload, X } from "lucide-react";
import { CustomLabel } from "../../../ui/labels";

const Wrapper = styled.div`
  width: 100%;
`;

const Dropzone = styled.div`
  position: relative;
  cursor: pointer;
  border-radius: 0.375rem;
  border: 2px dashed;
  padding: 1rem;
  text-align: center;
  transition: all 0.3s;
  border-color: ${(props) => (props.isDragging ? "#38bdf8" : "#d1d5db")};
  background-color: ${(props) => (props.isDragging ? "#e0f2fe" : "transparent")};

  &:hover {
    border-color: #9ca3af;
  }
`;

const FileInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 1rem;
`;

const FileName = styled.span`
  font-size: 0.875rem;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RemoveButton = styled.button`
  margin-left: 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
`;

const Placeholder = styled.div`
  padding: 1rem 0;

  p {
    margin-top: 0.25rem;
    font-size: 0.875rem;
  }
`;

const allowedTypes = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "application/pdf",
  "application/msword", // .doc
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
  "application/vnd.ms-excel", // .xls
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
  "application/vnd.ms-outlook", // .msg
];

const allowedExtensions = [".jpg", ".jpeg", ".png", ".gif", ".pdf", ".doc", ".docx", ".xls", ".xlsx", ".msg"];

export const validateFile = (file) => {
  const isMimeValid = allowedTypes.includes(file.type);
  const isExtValid = allowedExtensions.some((ext) => file.name.toLowerCase().endsWith(ext));

  if (!isMimeValid && !isExtValid) {
    return "Tipo de archivo no válido. Solo se permiten imágenes (jpg, png, gif), PDF, Excel, Word y archivos de Outlook (.msg).";
  }

  return null;
};

const FileUploader = ({ acceptedFiles = "*/*", fileValidatorFn, onError, onFilesChange, placeholder, prefilledFile, label, disabled }) => {
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
  };
  const addFiles = (newFiles) => {
    if (disabled) return;
    const hasInvalidFile = newFiles.some((file) => {
      const error = fileValidatorFn && fileValidatorFn(file);
      if (error) {
        if (onError) {
          onError(error !== true ? error : "Archivo inválido");
        }
        return true;
      }
      return false;
    });

    if (hasInvalidFile) return;

    setFiles([newFiles[0]]);
    if (onFilesChange) {
      onFilesChange([newFiles[0]]);
    }
  };
  const handleDrop = (e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  };

  const handleFileInput = (e) => {
    if (disabled) return;
    if (!e.target.files) return;

    const selectedFiles = Array.from(e.target.files);
    addFiles(selectedFiles);
    e.target.value = "";
  };

  const removeFile = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setFiles([]);
    if (onFilesChange) {
      onFilesChange([]);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    if (!prefilledFile || !Array.isArray(prefilledFile)) {
      setFiles([]);
      return;
    }

    // Solo actualiza si el archivo cambió por nombre
    if (!files.length || (files[0] && files[0].name !== prefilledFile[0] && prefilledFile[0].name)) {
      setFiles(prefilledFile);
    }
  }, [prefilledFile]);

  return (
    <Wrapper>
      {label && <CustomLabel htmlFor={label}>{label}</CustomLabel>}
      <Dropzone
        isDragging={isDragging}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => {
          if (disabled) return;
          fileInputRef.current.click();
        }}
        style={{ cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1 }}
      >
        <input type="file" ref={fileInputRef} onChange={handleFileInput} accept={acceptedFiles} disabled={disabled} style={{ display: "none" }} />
        {files.length > 0 ? (
          <FileInfo>
            <FileName>{files[0].originalname || files[0].name}</FileName>
            {!disabled && (
              <RemoveButton onClick={removeFile}>
                <X size={20} color="#FF0000" />
              </RemoveButton>
            )}
          </FileInfo>
        ) : (
          <Placeholder>
            <CloudUpload size={25} />
            <p>{placeholder || "Arrastra o selecciona un archivo"}</p>
          </Placeholder>
        )}
      </Dropzone>
    </Wrapper>
  );
};

export default FileUploader;
